// ContactList.tsx
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community"

import { ApiError, Contact, VibratoClient } from "@/lib/vibrato-api"
import { settings } from "@/settings"
import { Menu } from "@headlessui/react"
import UseCSV from "@usecsv/react"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { NavArrowDown } from "iconoir-react"
import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import ContactsTable from "./ContactsTable"
import { UserContext, VibratoClientContext } from "./contexts"
import CreateTaskTemplateCallModal from "./CreateTaskTemplateCallModal"

ModuleRegistry.registerModules([AllCommunityModule])

export default function ContactList(): React.JSX.Element {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const navigate = useNavigate()
  const user = useContext(UserContext)
  const [reloadCounter, setReloadCounter] = useState(0)
  const [showAddToCampaignModal, setShowAddToCampaignModal] = useState(false)
  const [selectedContacts, setSelectedContacts] = useState([])
  const [showCreateTaskTemplateCallModal, setShowCreateTaskTemplateCallModal] = useState(false)
  const [selectedContactForTaskTemplate, setSelectedContactForTaskTemplate] = useState<Contact | null>(null)
  const [showDeleteContactsModal, setShowDeleteContactsModal] = useState(false)

  useEffect(() => {
    document.title = "Vibrato - Contacts"
  }, [])

  const reloadContacts = () => {
    setReloadCounter(reloadCounter + 1)
  }

  const openCreateTaskTemplateCallModal = (contact: Contact) => {
    setSelectedContactForTaskTemplate(contact)
    setShowCreateTaskTemplateCallModal(true)
  }

  // const importCsvButton = (openModal: any) => {
  //   return (
  //     <button
  //       className="bg-blue-500 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg w-fit"
  //       onClick={openModal}
  //     >
  //       Import CSV
  //     </button>
  //   )
  // }

  const parseTags = (tags: string | null) => {
    if (!tags) {
      return []
    }

    return tags.split(",").map((tag) => tag.trim())
  }

  const sanitizePhoneNumber = (phoneNumber: string | null) => {
    if (!phoneNumber) {
      return ""
    }

    return phoneNumber.replace(/\D/g, "")
  }

  const sanitizeCountryCode = (countryCode: string | null) => {
    if (!countryCode) {
      return ""
    }

    return countryCode.replace(/\D/g, "")
  }

  const sanitizeContact = (contact: any) => {
    const fields: any[] = []

    Object.keys(contact).forEach((key) => {
      const value = contact[key]
      if (key === "tags") {
        contact[key] = parseTags(value)
      } else if (key === "phone_number") {
        contact[key] = sanitizePhoneNumber(value)
      } else if (key === "country_code") {
        contact[key] = sanitizeCountryCode(value)
      } else {
        contact[key] = value
      }

      if (!["tags", "phone_number", "country_code", "first_name", "last_name", "merge_key", "row"].includes(key)) {
        fields.push({
          key,
          value
        })

        delete contact[key]
      }
    })

    contact.custom_fields = fields

    return contact
  }

  async function validateAndProcessRecords(records: any) {
    console.log("validating records", records)

    const contacts = records.rows.map((record: any) => {
      let contact: { [key: string]: string | string[] } = {
        row: record.row
      }

      Object.keys(record.data).forEach((key) => {
        const value = record.data[key].value
        contact[key] = value
      })

      contact = sanitizeContact(contact)

      return contact
    })

    const validatedContacts: any = await vibratoClient.validateContacts(contacts)

    if (validatedContacts.valid) {
      console.log("contacts are valid")
      return null
    }

    console.log("validation errors detected", validatedContacts)

    const resp: any[] = []

    contacts.forEach((contact: { [key: string]: string | string[] }, index: number) => {
      const errors = validatedContacts.errors.contacts[index]

      if (Object.keys(errors).length > 0) {
        Object.keys(errors).forEach((errorKey) => {
          const colErrors: any[] = []

          errors[errorKey].forEach((error: string) => {
            colErrors.push({
              message: error,
              level: "error"
            })
          })

          resp.push({
            row: contact.row,
            data: {
              [errorKey]: colErrors
            }
          })
        })
      }
    })

    return resp
  }

  async function validateAndProcessRecord(record: any) {
    console.log("validating record", record)
    const resp = await validateAndProcessRecords({ rows: [record.row] })

    if (resp && resp.length > 0) {
      return resp[0]
    }

    return null
  }

  async function importContacts(records: any) {
    const rows = records.rows.map((record: any) => {
      delete record.row
      return sanitizeContact(record)
    })

    const resp = await vibratoClient.batchCreateContacts(rows)
    console.log("created contacts", resp)
  }

  const DeleteContactsModal = () => (
    <Modal show={showDeleteContactsModal} onClose={() => setShowDeleteContactsModal(false)} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-red-600">Confirm Deletion</span>
      </ModalHeader>
      <ModalBody>Are you sure you want to delete the selected contacts? This action cannot be undone.</ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2">
          <button
            className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
            onClick={() => setShowDeleteContactsModal(false)}
          >
            Cancel
          </button>
          <button
            className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded w-fit"
            onClick={async () => {
              if (selectedContacts.length > 0) {
                try {
                  await Promise.all(
                    selectedContacts.map((contact: Contact) => vibratoClient.deleteContact(contact.uuid))
                  )
                  setShowDeleteContactsModal(false)
                  setSelectedContacts([])
                  reloadContacts()
                } catch (error) {
                  console.error("error deleting contacts", error)
                }
              }
            }}
          >
            Delete
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )

  return (
    <div className="p-8 h-full w-full flex flex-col gap-4">
      <AddToCampaignModal
        show={showAddToCampaignModal}
        onClose={() => setShowAddToCampaignModal(false)}
        onConfirm={() => {}}
        contacts={selectedContacts}
      />

      <CreateTaskTemplateCallModal
        show={showCreateTaskTemplateCallModal}
        onClose={() => setShowCreateTaskTemplateCallModal(false)}
        contact={selectedContactForTaskTemplate}
      />

      <DeleteContactsModal />

      <div className="flex gap-2 justify-between items-center">
        <h1 className="text-2xl font-bold w-full md:w-auto">Contacts</h1>
        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button className="inline-flex justify-center items-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
            Actions
            <NavArrowDown className="ml-2 h-5 w-5" aria-hidden="true" />
          </Menu.Button>

          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm w-full text-left`}
                    onClick={() => navigate("/contacts/new")}
                  >
                    Create Contact
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <UseCSV
                    importerKey={settings.USE_CSV_IMPORTER_KEY}
                    user={{ user_id: user.id, email: user.email }}
                    render={(openModal) => (
                      <button
                        className={`${
                          active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                        } block px-4 py-2 text-sm w-full text-left`}
                        onClick={openModal}
                      >
                        Import CSV
                      </button>
                    )}
                    onData={async (records) => {
                      try {
                        await importContacts(records)
                        reloadContacts()
                      } catch (error) {
                        console.error("error importing contacts", error)

                        if (error instanceof ApiError) {
                          if (error.data && error.data.length && typeof error.data[0] === "string") {
                            const errorMessage = `Error saving contact: ${error.data.join("\n")}`

                            return Promise.resolve({
                              errors: records.rows.map((_row: any, index: number) => ({
                                row: index,
                                error: errorMessage
                              }))
                            })
                          } else if (error.data) {
                            return Promise.resolve({
                              errors: records.rows.map((_row: any, index: number) => ({
                                row: index,
                                // @ts-ignore
                                error: JSON.stringify(error.data[index])
                              }))
                            })
                          }
                        } else {
                          return Promise.resolve({
                            errors: records.rows.map((_row: any, index: number) => ({
                              row: index,
                              error: "Error saving contact"
                            }))
                          })
                        }
                      }
                    }}
                    // @ts-ignore
                    onRecordsInitial={async (records) => {
                      try {
                        const validation = await validateAndProcessRecords(records)
                        return validation
                      } catch (error) {
                        console.error("error validating records", error, records)
                      }

                      return null
                    }}
                    onRecordEdit={async (record) => {
                      try {
                        return await validateAndProcessRecord(record)
                      } catch (error) {
                        console.error("error validating record", error, record)
                      }

                      return null
                    }}
                    onError={(error) => {
                      console.error("error", error)
                      alert(`Error importing CSV: ${error}`)
                    }}
                  />
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm w-full text-left`}
                    onClick={() => setShowAddToCampaignModal(true)}
                  >
                    Add Selected to Campaign
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } block px-4 py-2 text-sm w-full text-left`}
                    onClick={() => setShowDeleteContactsModal(true)}
                  >
                    Delete Selected
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
      </div>
      <div className="font-thin">
        <p>
          Contacts are people, business, or organizations you can call. Use them to define who you want to call and how
          to reach them. You can add fields to contacts to store additional information about your contacts, such as
          email addresses or physical addresses.
        </p>
      </div>

      <ContactsTable
        vibratoClient={vibratoClient}
        reloadCounter={reloadCounter}
        setSelectedContacts={setSelectedContacts}
        openCreateTaskTemplateCallModal={openCreateTaskTemplateCallModal}
        showCreateCall
      />
    </div>
  )
}

function AddToCampaignModal({
  show,
  onClose,
  onConfirm,
  contacts
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
  contacts: any
}) {
  const vibratoClient = useContext(VibratoClientContext)
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [validationError, setValidationError] = useState("")
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaignUuid, setSelectedCampaignUuid] = useState("")
  const [includeProcessedContacts, setIncludeProcessedContacts] = useState(false)

  const onAdd = async () => {
    if (!selectedCampaignUuid) {
      setError("Please select a campaign")
      return
    }

    if (contacts.length === 0) {
      setError("No contacts selected")
      return
    }

    const contactUuids = contacts.map((contact: any) => contact.uuid)

    try {
      const resp = await vibratoClient.batchAddContactsToCampaign(
        selectedCampaignUuid,
        contactUuids,
        includeProcessedContacts
      )

      const newCampaign = await vibratoClient.getCampaign(selectedCampaignUuid)
      if (newCampaign.validation_error) {
        setValidationError(
          `Success: ${resp?.message}.\n\nWarning: Not all contacts are ready to be called. Please review the campaign for more information.`
        )
      } else {
        setMessage(`Success: ${resp?.message}`)
      }

      setError("")
    } catch (error: any) {
      if (error?.data?.contact_uuids) {
        let msg = "Error adding contacts to campaign:\n"
        // iterate through contacts with index
        contacts.forEach((contact: any, index: any) => {
          if (error.data.contact_uuids[index]) {
            msg += `- ${contact.first_name} `
            if (contact.last_name) {
              msg += `${contact.last_name} `
            }
            if (contact.merge_key) {
              msg += `(${contact.merge_key}): `
            }

            msg += `${error.data.contact_uuids[index]}\n`
          }
        })

        setError(msg)
      } else {
        setError("Error adding contacts to campaign.")
      }

      return
    }

    await onConfirm()
  }

  const wrappedOnClose = () => {
    setMessage("")
    setError("")
    setValidationError("")
    setSelectedCampaignUuid("")
    setIncludeProcessedContacts(false)
    onClose()
  }

  useEffect(() => {
    vibratoClient.getCampaigns().then((campaigns: any) => {
      setCampaigns(campaigns)
    })
  }, [])

  return (
    <Modal show={show} onClose={wrappedOnClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-purple-600">Add {contacts.length} Contact(s) to Campaign</span>
      </ModalHeader>
      <ModalBody>
        <label htmlFor="campaign-selection" className="block text-sm font-medium text-gray-700">
          Campaign
        </label>
        <select
          value={selectedCampaignUuid}
          onChange={(e) => setSelectedCampaignUuid(e.target.value)}
          className="mt-2 mb-4 p-2 border rounded w-full"
        >
          <option value="">Select Campaign</option>
          {campaigns.map((campaign: any) => (
            <option key={campaign.uuid} value={campaign.uuid}>
              {campaign.name}
            </option>
          ))}
        </select>
        <div className="flex items-center mt-4">
          <input
            id="include-processed-checkbox"
            type="checkbox"
            checked={includeProcessedContacts}
            onChange={(e) => setIncludeProcessedContacts(e.target.checked)}
            className="w-4 h-4 text-purple-600 bg-gray-100 rounded border-gray-300 focus:ring-purple-500"
          />
          <label htmlFor="include-processed-checkbox" className="ml-2 text-sm font-medium text-gray-900">
            Add contacts currently in the campaign (warning: contacts will be called again)
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex flex-col gap-2">
          {!message && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={wrappedOnClose}
                className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onAdd}
                className="mt-2 bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded w-fit"
              >
                Add
              </button>
            </div>
          )}
          {message && <div className="text-green-500">{message}</div>}
          {error && <div className="text-red-500 whitespace-pre-wrap">{error}</div>}
          {validationError && <div className="text-yellow-500 whitespace-pre-wrap">{validationError}</div>}
        </div>
      </ModalFooter>
    </Modal>
  )
}
